import { isHttpStatusAuthSessionFailure } from "@Front/Api/helpers/httpAuthSessionFailurePolicy";
import { isUserAbortError } from "@Front/Utils/networkTimeoutHelpers";
import { handleAuthSessionFailureForEndpoint } from "@Front/Api/helpers/authSessionFailureHandlingHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { getUserFriendlyError } from "@Front/Utils/errorMessages";
import { userFacingStringFromMessageThenReason } from "@Front/Utils/httpValidationErrorUserMessage";
import type { ApiError, ApiRequestOptions } from "@Front/Hooks/useApiClientTypes";

export function buildRequestHeaders(
	requireAuth: boolean,
	getAccessToken: (endpoint?: string) => string | null,
	additionalHeaders?: HeadersInit,
	endpoint?: string,
): Headers {
	const headers = new Headers(additionalHeaders);

	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}

	if (requireAuth) {
		const token = getAccessToken(endpoint);
		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}
	}

	return headers;
}

async function parseApiErrorResponse(
	response: Response,
	errorMessage?: string,
): Promise<{ message: string; errorData: unknown }> {
	let message = errorMessage || `Erreur ${response.status}: ${response.statusText}`;
	let errorData: unknown = undefined;

	try {
		const responseText = await response.text();
		if (responseText) {
			errorData = JSON.parse(responseText);
			if (errorData && typeof errorData === "object") {
				const fromBody = userFacingStringFromMessageThenReason(
					errorData as { message?: unknown; reason?: unknown },
				);
				if (fromBody) {
					message = fromBody;
				}
			}
		}
	} catch {
		// Ignore parsing errors, use default message
	}

	return { message, errorData };
}

function createApiError(message: string, status: number, errorData: unknown): ApiError {
	const error = new Error(message) as ApiError;
	error.status = status;
	error.data = errorData;
	return error;
}

async function handleCriticalErrors(httpStatus: number, endpoint?: string): Promise<void> {
	if (typeof window === "undefined") return;
	if (!isHttpStatusAuthSessionFailure(httpStatus)) return;
	try {
		await handleAuthSessionFailureForEndpoint(httpStatus, endpoint, "useApiClient");
	} catch (error) {
		LoggerService.error("❌ [useApiClient] Error during session failure handling", { error });
	}
}

export async function handleApiErrorResponse(
	response: Response,
	errorMessage?: string,
	logErrors: boolean = true,
): Promise<ApiError> {
	const httpStatus = response.status;
	const responseUrl = typeof response.url === "string" && response.url.length > 0 ? response.url : undefined;
	if (isHttpStatusAuthSessionFailure(httpStatus)) {
		await handleCriticalErrors(httpStatus, responseUrl);
	}

	const { message, errorData } = await parseApiErrorResponse(response, errorMessage);

	if (logErrors) {
		const logPayload: Record<string, unknown> = {
			status: response.status,
			statusText: response.statusText,
			message,
			errorData,
		};
		if (response.status === 502 || response.status >= 500) {
			logPayload["url"] = response.url;
		}
		LoggerService.error("❌ [useApiClient] API request failed", logPayload);
	}

	return createApiError(message, response.status, errorData);
}

export function buildRequestInit(
	method: "GET" | "POST" | "PUT" | "DELETE",
	body: unknown | undefined,
	headers: Headers,
	fetchOptions: Omit<ApiRequestOptions, "requireAuth" | "errorMessage" | "logErrors">,
): RequestInit {
	const init: RequestInit = {
		...fetchOptions,
		method,
		headers,
	};

	if (body !== undefined && method !== "GET") {
		init.body = JSON.stringify(body);
	}

	return init;
}

export async function parseResponseData<T>(response: Response): Promise<T> {
	const contentType = response.headers.get("content-type") || "";
	const expectsJson = contentType.includes("application/json");
	return expectsJson ? ((await response.json()) as T) : ((await response.text()) as unknown as T);
}

export async function handleRequestFailureAsNever(
	error: unknown,
	method: string,
	endpoint: string,
	logErrors: boolean,
): Promise<never> {
	if (isUserAbortError(error)) {
		throw error;
	}
	if (error instanceof Error && "status" in error) {
		throw error;
	}

	const friendlyError = await getUserFriendlyError(error);
	if (logErrors) {
		LoggerService.error(`❌ [useApiClient] ${method} request failed`, { error, endpoint });
	}
	throw new Error(friendlyError.message);
}
